/**
 * Sérialisation de l'état d'un îlot.
 *
 * Une sauvegarde porte un numéro de version : le jour où le modèle d'état
 * changera, une classe en cours d'année ne doit pas perdre sa partie. Les
 * migrations se déclarent dans `MIGRATIONS`, de la version N à la version N+1.
 */
import { z } from 'zod';

import {
  CHAPTER_IDS,
  chainRoleSchema,
  chapterIdSchema,
  connectedUsefulnessSchema,
  gaugeIdSchema,
  reglageProgrammeSchema,
} from './content-schema.js';
import { JOURNAL_EVENT_TYPES } from './journal.js';
import type { GameState } from './state.js';
import { MAX_TEXTE_QUESTIONNAIRE, NIVEAUX_QUESTIONNAIRE, refus } from './state.js';
import type { ActionRefusal } from './state.js';

/** Version du format de sauvegarde. À incrémenter dès que `GameState` change. */
export const SAVE_VERSION = 6;

const idSchema = z.string().min(1);

const actionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('creer_partie'), islandCode: z.string() }).strict(),
  z.object({ type: z.literal('decouvrir_probleme'), problemId: idSchema }).strict(),
  z
    .object({ type: z.literal('classer_probleme'), problemId: idSchema, categoryId: idSchema })
    .strict(),
  z.object({ type: z.literal('valider_classement'), corrects: z.array(idSchema) }).strict(),
  z
    .object({
      type: z.literal('analyser_probleme'),
      problemId: idSchema,
      reponse: connectedUsefulnessSchema,
    })
    .strict(),
  z.object({ type: z.literal('definir_priorites'), priorities: z.array(idSchema) }).strict(),
  z
    .object({ type: z.literal('placer_equipement'), slot: chainRoleSchema, equipmentId: idSchema })
    .strict(),
  z
    .object({ type: z.literal('retirer_equipement'), slot: chainRoleSchema, equipmentId: idSchema })
    .strict(),
  z.object({ type: z.literal('vider_chaine') }).strict(),
  z
    .object({
      type: z.literal('repondre_question_guidee'),
      questionId: idSchema,
      choiceId: idSchema,
    })
    .strict(),
  z.object({ type: z.literal('regler_programme'), reglage: reglageProgrammeSchema }).strict(),
  z
    .object({
      type: z.literal('organiser_tournee'),
      jour: z.string(),
      conteneurs: z.array(idSchema),
    })
    .strict(),
  z.object({ type: z.literal('revenir_a_concevoir') }).strict(),
  z
    .object({ type: z.literal('deployer'), solutionId: idSchema.nullable(), cout: z.number() })
    .strict(),
  z
    .object({
      type: z.literal('resoudre_evenement'),
      eventId: idSchema,
      choiceId: idSchema.nullable(),
    })
    .strict(),
  z
    .object({
      type: z.literal('ecrire_trace'),
      chapterId: chapterIdSchema,
      fieldId: idSchema,
      valeur: z.string(),
    })
    .strict(),
  z.object({ type: z.literal('aller_etape'), stepIndex: z.number().int() }).strict(),
  z.object({ type: z.literal('changer_chapitre'), chapterId: chapterIdSchema }).strict(),
  z.object({ type: z.literal('repondre_questionnaire'), chapterId: chapterIdSchema }).strict(),
]);

const journalEntrySchema = z
  .object({
    t: z.number().int().nonnegative(),
    type: z.enum(JOURNAL_EVENT_TYPES),
    chapitre: chapterIdSchema,
    etape: z.number().int().nonnegative(),
    detail: z.string().nullable(),
  })
  .strict();

const questionnaireSchema = z
  .object({
    compris: z.enum(NIVEAUX_QUESTIONNAIRE),
    aime: z.enum(NIVEAUX_QUESTIONNAIRE),
    difficile: z.string().max(MAX_TEXTE_QUESTIONNAIRE),
  })
  .strict();

const chainStateSchema = z
  .object({
    acquerir: z.array(idSchema),
    traiter: z.array(idSchema),
    communiquer: z.array(idSchema),
    agir: z.array(idSchema),
  })
  .strict();

const gameStateSchema = z
  .object({
    islandCode: z.string(),
    chapterId: chapterIdSchema,
    stepIndex: z.number().int().nonnegative(),
    gauges: z.record(gaugeIdSchema, z.number()),
    discoveredProblems: z.array(idSchema),
    classification: z.record(z.string(), z.string()),
    classificationAttempts: z.record(z.string(), z.number().int().nonnegative()),
    connectedAnalysis: z.record(z.string(), connectedUsefulnessSchema),
    priorities: z.array(idSchema),
    chain: chainStateSchema,
    deployedSolutionId: idSchema.nullable(),
    gaugesBeforeDeploy: z.record(gaugeIdSchema, z.number()).nullable(),
    redesignsUsed: z.number().int().nonnegative(),
    guidedAnswers: z.record(z.string(), z.string()),
    programme: reglageProgrammeSchema.nullable(),
    tournees: z.record(z.string(), z.array(idSchema)),
    triggeredEvents: z.array(idSchema),
    eventChoices: z.record(z.string(), z.string()),
    writtenTrace: z.record(chapterIdSchema, z.record(z.string(), z.string())),
    score: z.record(chapterIdSchema, z.number()),
    history: z.array(actionSchema),
    journal: z.array(journalEntrySchema),
    questionnaires: z.partialRecord(chapterIdSchema, questionnaireSchema),
  })
  .strict();

/**
 * L'enveloppe, et **rien de plus**.
 *
 * L'état n'est volontairement pas validé ici : une sauvegarde d'une version
 * antérieure n'a pas la forme courante, c'est tout l'intérêt des migrations. La
 * valider trop tôt rejetterait précisément les parties qu'on cherche à sauver.
 */
const enveloppeSchema = z.looseObject({
  version: z.number().int().positive(),
  state: z.unknown(),
});

/** Les types d'action couverts par le schéma, pour vérifier qu'aucun ne manque. */
export const ACTION_TYPES_IN_SCHEMA: readonly string[] = actionSchema.options.map(
  (option) => option.shape.type.value,
);

/** Une migration fait passer l'état d'une version à la suivante. */
export type SaveMigration = (state: unknown) => unknown;

/** Migrations successives, indexées par la version d'origine. */
export const MIGRATIONS: Readonly<Record<number, SaveMigration>> = {
  /**
   * 1 → 2 : arrivée de `classificationAttempts`, qui compte les secondes chances
   * au classement du chapitre 1. Une partie d'avant n'en a jamais utilisé.
   */
  1: (state) => ({ ...(state as object), classificationAttempts: {} }),

  /**
   * 2 → 3 : arrivée du chapitre 2 — mémoire des jauges d'avant déploiement,
   * compteur de retours à l'étape Concevoir, et réponses aux questions guidées.
   */
  2: (state) => ({
    ...(state as object),
    gaugesBeforeDeploy: null,
    redesignsUsed: 0,
    guidedAnswers: {},
  }),

  /**
   * 3 → 4 : instrumentation du pilote en classe — journal de séance et
   * questionnaire de fin de chapitre. Une partie d'avant n'en a ni l'un ni
   * l'autre, et c'est sans conséquence sur le jeu.
   */
  3: (state) => ({ ...(state as object), journal: [], questionnaires: {} }),

  /**
   * 4 → 5 : arrivée du chapitre 3.
   *
   * `writtenTrace` et `score` sont indexés par chapitre, et `z.record` sur une
   * énumération est **exhaustif** : une partie d'avant, qui ne connaît que
   * « ch1 » et « ch2 », serait rejetée à la relecture pour une clé « ch3 »
   * manquante. Autrement dit, sans cette migration, toutes les parties en cours
   * devenaient illisibles le jour du déploiement.
   *
   * On complète les chapitres absents plutôt que d'écraser : une partie du
   * futur, relue par un serveur en retard, garderait ainsi ce qu'elle a.
   */
  4: (state) => {
    const ancien = state as {
      writtenTrace?: Record<string, unknown>;
      score?: Record<string, unknown>;
    };
    const complete = <T>(
      existant: Record<string, unknown> | undefined,
      valeurParDefaut: T,
    ): Record<string, T> => {
      const rempli: Record<string, T> = {};
      for (const chapitre of CHAPTER_IDS) {
        rempli[chapitre] = (existant?.[chapitre] as T | undefined) ?? valeurParDefaut;
      }
      return { ...(existant as Record<string, T> | undefined), ...rempli };
    };

    return {
      ...(state as object),
      // Le chapitre 3 apporte aussi le programme réglé par l'îlot : « null »
      // veut dire qu'il n'y a pas encore touché, ce qui est vrai de toute
      // partie antérieure.
      programme: null,
      writtenTrace: complete(ancien.writtenTrace, {} as Record<string, string>),
      score: complete(ancien.score, 0),
    };
  },

  /**
   * 5 → 6 : arrivée du chapitre 4.
   *
   * Même mécanisme qu'au chapitre précédent : « ch4 » entre dans
   * `CHAPTER_IDS`, donc `writtenTrace` et `score` doivent le porter, sans quoi
   * toute partie antérieure devient illisible. S'y ajoute la table des
   * tournées, vide pour qui n'a jamais vu ce chapitre.
   */
  5: (state) => {
    const ancien = state as {
      writtenTrace?: Record<string, unknown>;
      score?: Record<string, unknown>;
    };
    const complete = <T>(
      existant: Record<string, unknown> | undefined,
      valeurParDefaut: T,
    ): Record<string, T> => {
      const rempli: Record<string, T> = {};
      for (const chapitre of CHAPTER_IDS) {
        rempli[chapitre] = (existant?.[chapitre] as T | undefined) ?? valeurParDefaut;
      }
      return { ...(existant as Record<string, T> | undefined), ...rempli };
    };

    return {
      ...(state as object),
      tournees: {},
      writtenTrace: complete(ancien.writtenTrace, {} as Record<string, string>),
      score: complete(ancien.score, 0),
    };
  },
};

export type MigrationResult = { readonly ok: true; readonly state: unknown } | ActionRefusal;

/**
 * Fait remonter un état sauvegardé jusqu'à la version cible, migration après
 * migration. Fonction à part pour être testable sans fabriquer de fausse
 * sauvegarde du futur.
 */
export function migrateSavedState(
  version: number,
  state: unknown,
  targetVersion: number = SAVE_VERSION,
  migrations: Readonly<Record<number, SaveMigration>> = MIGRATIONS,
): MigrationResult {
  let versionCourante = version;
  let etat = state;

  while (versionCourante < targetVersion) {
    const migration = migrations[versionCourante];
    if (!migration) {
      return refus(
        'sauvegarde_version_inconnue',
        `Aucune migration de la version ${versionCourante} vers la version ${versionCourante + 1}.`,
      );
    }
    etat = migration(etat);
    versionCourante += 1;
  }

  if (versionCourante > targetVersion) {
    return refus(
      'sauvegarde_version_inconnue',
      `Sauvegarde en version ${versionCourante}, alors que ce jeu lit au plus la version ${targetVersion}. Mettre le jeu à jour.`,
    );
  }

  return { ok: true, state: etat };
}

/** Sérialise l'état d'un îlot, prêt à être sauvegardé. */
export function serialize(state: GameState): string {
  return JSON.stringify({ version: SAVE_VERSION, state });
}

export type DeserializeResult = { readonly ok: true; readonly state: GameState } | ActionRefusal;

/**
 * Relit une sauvegarde : analyse du JSON, migrations éventuelles, puis
 * validation par le schéma. Ne lève jamais : une sauvegarde abîmée doit
 * pouvoir être signalée à l'élève, pas faire planter la page.
 */
export function deserialize(raw: string): DeserializeResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return refus('sauvegarde_illisible', "La sauvegarde n'est pas un JSON valide.");
  }

  const enveloppe = enveloppeSchema.safeParse(parsed);
  if (!enveloppe.success) {
    const premier = enveloppe.error.issues[0];
    return refus(
      'sauvegarde_illisible',
      premier
        ? `Sauvegarde invalide (${premier.path.join('.') || 'racine'}) : ${premier.message}`
        : 'Sauvegarde invalide.',
    );
  }

  const migre = migrateSavedState(enveloppe.data.version, enveloppe.data.state);
  if (!migre.ok) {
    return migre;
  }

  const valide = gameStateSchema.safeParse(migre.state);
  if (!valide.success) {
    const premier = valide.error.issues[0];
    return refus(
      'sauvegarde_illisible',
      premier
        ? `Sauvegarde invalide (${premier.path.join('.') || 'racine'}) : ${premier.message}`
        : 'Sauvegarde invalide.',
    );
  }

  return { ok: true, state: valide.data as GameState };
}
